import { ActionContext } from "vuex";
import { LoggerService as lgg } from "vue-components-library";

import { VISIBLE_EVENTS } from "@/Case/config";
import { CaseService, ICase } from "@/Case/services";
import { shouldAllowCaseAccess } from "@/Case/store/functions/shouldAllowCaseAccess";
import { hasEMSAgencyAccessLevel } from "@/Case/store/functions/hasEmsAgencyAcessLevel";
import { isSelectedEMSAgencyAPreviousAgency } from "@/Case/store/functions/isSelectedEMSAgencyAPreviousAgency";
import { AccessLevel } from "@/Case/store/functions/AccessLevel";

const LOG_TAG = "[CASE STORE]";

function getDefaultState(): { twiageCase: ICase; messages: unknown[]; uploads: never[] } {
  return {
    twiageCase: {} as ICase,
    messages: [],
    uploads: []
  };
}

const state = getDefaultState();

const getters = {
  caseTimelineEvents: (state: any) => {
    const newEventItems = state.twiageCase.events
      .filter((event: any) => VISIBLE_EVENTS.includes(event.type))
      .map((item: any) => {
        const { type, data } = item;
        const title = type;
        const DATA_BY_TYPE: { [type: string]: any } = {
          "CLOSED BY EMS": "",
          "SET GENDER": data == "f" ? "Female" : "Male"
        };

        return {
          from: new Date(item.createdAt),
          title,
          data: type in DATA_BY_TYPE ? DATA_BY_TYPE[type] : data,
          author: item.createdBy.displayName,
          type
        };
      })
      .concat(
        state.uploads.filter((item: any) => {
          if (item.author == "Recording Bot") {
            return false; // skip
          }
          return true;
        })
      );

    const newMessageItems = !state.messages
      ? []
      : state.messages.map((item: any) => {
          const { content, senderName } = item;
          let type = "msgems";
          if (["Admin Default", "Twiage System"].includes(senderName)) {
            type = "msg";
          }
          return {
            from: new Date(item.timestamp),
            title: `${senderName} sent a message:`,
            data: content,
            author: senderName,
            type: type
          };
        });
    return newEventItems.concat(newMessageItems);
  },
  canViewCase: (state: any) => {
    if (shouldAllowCaseAccess(state.twiageCase.type)) return true;

    return !hasEMSAgencyAccessLevel(
      state.twiageCase.destinationHospital.emsAgenciesAccess,
      state.twiageCase.emsAgency.id,
      AccessLevel.NO_ACCESS
    );
  },
  canPDFCase: (state: any, getters?, rootState?) => {
    if (shouldAllowCaseAccess(state.twiageCase.type) || state.twiageCase?.destinationHospital?.nonTwiage) return true;

    if (
      hasEMSAgencyAccessLevel(
        state.twiageCase.destinationHospital.emsAgenciesAccess,
        state.twiageCase.emsAgency.id,
        AccessLevel.FULL_ACCESS
      )
    ) {
      return true;
    }

    // need to write a test for this one
    // like: `it("A Non-Twiage Hospital", () => {` @Case.Store.spec.ts:611
    // and change other tests accordingly, if need be
    if (rootState) {
      const { id: selectedEMSAgencyId } = rootState.Login.selectedEMSAgency;
      if (isSelectedEMSAgencyAPreviousAgency(selectedEMSAgencyId, state.twiageCase.previousEMSAgencies)) {
        return true;
      }
    }

    return false;
  },
  messages: (state: any) => state.messages,
  uploads: (state: any) => state.uploads
};

const actions = {
  async findById(context: ActionContext<any, any>, id: String) {
    try {
      context.dispatch("Global/loading", null, { root: true });
      const twiageCase = await CaseService.findById(id);
      context.commit("SET_TWIAGE_CASE", twiageCase);

      twiageCase.uploads.forEach((upload: any) => {
        context.commit("ADD_UPLOAD", upload);
        context.dispatch("attachUploadData", upload);
      });
    } catch (error) {
      lgg.error(`${LOG_TAG} Failed to load Case`, { error });
      throw error;
    } finally {
      context.dispatch("Global/doneLoading", null, { root: true });
    }
  },
  getMessages(context: ActionContext<any, any>) {
    context.commit("SET_TWIAGE_CASE_MESSAGES");
  },
  resetState(context: ActionContext<any, any>) {
    context.commit("RESET_STATE");
  },

  async attachUploadData(context: ActionContext<any, any>, upload: any) {
    const { state } = context;

    const UPLOAD_TO_BLOB_TYPE: any = {
      photo: "image/png",
      video: "video/mp4",
      voicememo: "",
      default: "image/png"
    };
    const blobType =
      upload.type in UPLOAD_TO_BLOB_TYPE ? UPLOAD_TO_BLOB_TYPE[upload.type] : UPLOAD_TO_BLOB_TYPE["default"];

    const response = await CaseService.getUploadFile(state.twiageCase.id, upload.id);
    const data = URL.createObjectURL(new Blob([response], { type: blobType }));

    context.commit("UPDATE_UPLOAD", { id: upload.id, data });
  },

  async openCasePDF(context): Promise<void> {
    try {
      context.dispatch("Global/loading", null, { root: true });
      const url = await CaseService.getCasePDF(context.state.twiageCase.id);
      window.open(url);
    } catch (error) {
      lgg.error(`${LOG_TAG} Case PDF error`, { error });
    } finally {
      context.dispatch("Global/doneLoading", null, { root: true });
    }
  }
};

const mutations = {
  LOADING(state: any) {
    state.isLoading = true;
  },
  DONE_LOADING(state: any) {
    state.isLoading = false;
  },
  SET_TWIAGE_CASE(state: any, twiageCase: ICase) {
    state.twiageCase = CaseService.augmentData(twiageCase);
  },
  ADD_UPLOAD(state: any, upload: any) {
    state.uploads.push({
      id: upload.id,
      from: new Date(upload.createdAt),
      title: upload.label === "voice call" ? "ED CALL" : `ADDED ${upload.type.toUpperCase()}`,
      data: null,
      author: upload.createdBy.displayName,
      type: upload.type
    });
  },
  UPDATE_UPLOAD(state: any, updatedUpload: any) {
    state.uploads = state.uploads.map((upload: any) => {
      return upload.id === updatedUpload.id ? { ...upload, ...updatedUpload } : upload;
    });
  },
  SET_TWIAGE_CASE_MESSAGES(state: any) {
    state.messages = state.twiageCase.messages;
  },
  RESET_STATE(state: any) {
    const initialState: any = getDefaultState();

    Object.keys(initialState).forEach(key => {
      state[key] = initialState[key];
    });
  }
};

const store = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};

export default store;
